import { Avatar, AvatarGroup, Box, Divider, Stack, Tooltip, Typography } from '@mui/material';

/**
 * One person currently viewing a page.
 *
 * `name` is already resolved by the caller — display name, else email, which every user has. The
 * component never invents a placeholder, because "Unknown" reads as a real person's name.
 */
export interface CollaboratorPresence {
  /** Stable React key. Callers with multiple connections per person dedupe before passing them in. */
  key: string;
  id?: number | string;
  name: string;
  /** The collab palette colour — `getUserCollabColor` from `@utils/collaborationUserColor`. */
  color: string;
  avatarUrl?: string | null;
  isCurrentUser: boolean;
}

export interface CollaboratorAvatarsProps {
  users: CollaboratorPresence[];
  /** Tooltip heading, e.g. "Currently viewing". Callers translate it. */
  title: string;
  /** Suffix marking the signed-in user, e.g. "(You)". Callers translate it. */
  currentUserSuffix: string;
  max?: number;
  'data-testid'?: string;
}

const DEFAULT_MAX_AVATARS = 5;

/** Initials from an already-resolved display name — first letters of the first two words. */
export const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'A';
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
};

/**
 * The "who else is here" avatar strip: an overlapping avatar group under a single tooltip that
 * lists everyone by name, the signed-in user first and marked.
 *
 * ONE component so the document editor and the data-analysis workspace cannot drift apart — they
 * previously had different tooltips, different colour sources and different name fallbacks, which
 * made the same concept look like two features. Presentational only: callers own the transport
 * (Yjs awareness in the editor, a socket presence room in data analysis), the ordering and the
 * name resolution.
 */
export function CollaboratorAvatars({
  users,
  title,
  currentUserSuffix,
  max = DEFAULT_MAX_AVATARS,
  'data-testid': testId,
}: CollaboratorAvatarsProps) {
  if (users.length === 0) return null;

  return (
    <Tooltip
      placement='bottom-end'
      title={
        <Stack spacing={1}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{title}</Typography>
          <Divider />
          <Stack spacing={0.75}>
            {users.map((user) => (
              <Box key={user.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  src={user.avatarUrl || undefined}
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: 10,
                    fontWeight: 600,
                    backgroundColor: user.color,
                  }}
                >
                  {initialsFromName(user.name)}
                </Avatar>
                <Typography sx={{ fontSize: 12, fontWeight: user.isCurrentUser ? 600 : 500 }}>
                  {user.name}
                  {user.isCurrentUser ? ` ${currentUserSuffix}` : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      }
      slotProps={{ tooltip: { sx: { color: 'inherit', backgroundColor: '#fff' } } }}
    >
      <AvatarGroup
        max={max}
        aria-label={title}
        data-testid={testId}
        sx={{ marginRight: 0.5, '& .MuiAvatar-root': { border: '1px solid #fff' } }}
      >
        {users.map((user) => (
          <Avatar
            key={user.key}
            src={user.avatarUrl || undefined}
            sx={{
              backgroundColor: user.color,
              ...(user.avatarUrl ? { outline: `1px solid ${user.color}`, outlineOffset: 1 } : {}),
            }}
          >
            {initialsFromName(user.name)}
          </Avatar>
        ))}
      </AvatarGroup>
    </Tooltip>
  );
}
